import React from 'react';
import { v4 } from 'uuid';
import DocumentItemsFormElement, { DocumentItemsFormElementSharedProps } from './DocumentItemsFormElement';
import {
  fromInvoiceItem, Item, DocumentAvailability, bindingLinePrice, suggestedResalePrice,
} from '../models/item';
import { InvoiceItem } from '../models/invoice';
import usePricingSettings from '../hooks/usePricingSettings';
import useUsers from '../hooks/useUsers';
import { AccessRole } from '../models/user';
import { DEFAULT_PRICING_SETTINGS } from '../models/pricingSettings';

const makeItemFromCatalog = (
  newItem: Item,
  category: string,
  defaultGroupMarkup = DEFAULT_PRICING_SETTINGS.groupMarkup,
  defaultSrpMarkup = DEFAULT_PRICING_SETTINGS.srpMarkup,
): InvoiceItem => ({
  costSubtotal: 0,
  costTotal: 0,
  createdBy: undefined,
  createdById: "",
  createdDate: "",
  employee: undefined,
  employeeId: "",
  invoice: undefined,
  invoiceId: "",
  isLocked: false,
  item: undefined,
  lastModifiedBy: undefined,
  lastModifiedById: "",
  lastModifiedDate: "",
  retailSubtotal: 0,
  retailTotal: 0,
  tax: 0,
  taxTotal: 0,
  vendor: undefined,
  brand: newItem.brand,
  category,
  costEach: newItem.costEach,
  description: newItem.description,
  id: v4(),
  itemId: newItem.id,
  quantity: 1,
  // item 422 — hardware: marked-up group price (never cost/raw retail); services:
  // the entered group price
  retailEach: bindingLinePrice(newItem),
  // item 436 — the MSRP pair rides invoices too (editor-only; the PDF bills group price)
  listPrice: suggestedResalePrice(newItem, defaultGroupMarkup, defaultSrpMarkup),
  isHardware: newItem.isHardware ?? false,
  sku: newItem.sku,
  taxable: false, // dart (2026-07-15, Chad) — per-line "Add tax" opt-in; default OFF (retired the catalog taxable flag)
  vendorId: newItem.vendorId,
});

const InvoiceItemsFormElement: React.FC<DocumentItemsFormElementSharedProps<InvoiceItem>> = (props) => {
  const { pricing } = usePricingSettings(); // item 422 — company default markups
  const defaults = pricing ?? DEFAULT_PRICING_SETTINGS;
  // item 943 (QB AR-export epic) — the per-line "Performed by" dropdown: internal employees
  // only (externals never perform billable work). Sorted for a stable pick list.
  const { data: usersData } = useUsers();
  const performedByUsers = (usersData?.index ?? [])
    .filter((u: any) => u.isActive !== false && Number(u.accessRole) !== AccessRole.CustomerBuilder)
    .map((u: any) => ({ id: u.id, name: u.name ?? '' }))
    .sort((a: any, b: any) => a.name.localeCompare(b.name));
  return (
    <DocumentItemsFormElement<InvoiceItem>
      categoryNames={props.categoryNames}
      items={props.items}
      setItems={props.setItems}
      categoryButtonHandler={props.categoryButtonHandler}
      activeCategory={props.activeCategory}
      categoryEditMode={props.categoryEditMode}
      editCategoryName={props.editCategoryName}
      setEditCategoryName={props.setEditCategoryName}
      onAddClose={props.onAddClose}
      isAddOpen={props.isAddOpen}
      showCost
      showRetail
      showMsrpPair
      showGrpPair
      defaultGrpMult={defaults.groupMarkup}
      defaultMsrpMult={defaults.srpMarkup}
      performedByUsers={performedByUsers}
      showLineDate // item 1336 — the per-line T&M date
      showPullFromStock // item 1452 — FROM STOCK toggle on hardware lines (finalize pulls)
      availableOn={DocumentAvailability.Invoice}
      makeItemFromCatalog={(newItem, category) => makeItemFromCatalog(newItem, category, defaults.groupMarkup, defaults.srpMarkup)}
      toCatalogItem={fromInvoiceItem}
    />
  );
};

export default InvoiceItemsFormElement;
